import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axios/axiosInstance';

const ALL_FEATURES = [
    { key: 'dashboard',               label: 'Dashboard',                  group: 'General' },
    { key: 'run_payroll',             label: 'Run Payroll',                group: 'Payroll' },
    { key: 'view_payroll_history',    label: 'View Payroll History',       group: 'Payroll' },
    { key: 'approve_payroll',         label: 'Approve Payroll',            group: 'Payroll' },
    { key: 'manage_employees',        label: 'Manage Employees',           group: 'People' },
    { key: 'manage_contractors',      label: 'Manage Contractors',         group: 'People' },
    { key: 'benefits',                label: 'Benefits',                   group: 'People' },
    { key: 'deductions',              label: 'Deductions',                 group: 'People' },
    { key: 'time_off',                label: 'Time Off',                   group: 'People' },
    { key: 'time_attendance',         label: 'Time & Attendance',          group: 'People' },
    { key: 'reports',                 label: 'Reports',                    group: 'Reporting' },
    { key: 'manage_company',          label: 'Manage Company',             group: 'Admin' },
    { key: 'location_management',     label: 'Location Management',        group: 'Admin' },
    { key: 'user_management',         label: 'User Management / Onboarding', group: 'Admin' },
    { key: 'role_permissions',        label: 'Role & Permissions',         group: 'Admin' },
    { key: 'branding',                label: 'Branding',                   group: 'Admin' },
    { key: 'billing',                 label: 'Billing',                    group: 'Admin' },
    { key: 'accountnoot_integration', label: 'AccountNoot Integration',    group: 'Integrations' },
];

const GROUPS = [...new Set(ALL_FEATURES.map(f => f.group))];

const Spinner = () => (
    <div style={{ width: 18, height: 18, border: '2px solid #1B75F0', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
);

const RoleManagement = () => {
    const [roles, setRoles] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [checkedFeatures, setCheckedFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [showNewRole, setShowNewRole] = useState(false);
    const [toast, setToast] = useState(null);
    const [dirty, setDirty] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/roles');
            setRoles(res.data);
            if (res.data.length > 0 && !selectedId) {
                setSelectedId(res.data[0].id);
                setCheckedFeatures(res.data[0].features);
            }
        } catch {
            showToast('Failed to load roles.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchRoles(); }, []);

    const selectRole = (role) => {
        setSelectedId(role.id);
        setCheckedFeatures([...role.features]);
        setDirty(false);
    };

    const toggleFeature = (key) => {
        setCheckedFeatures(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
        setDirty(true);
    };

    const toggleGroup = (group) => {
        const groupKeys = ALL_FEATURES.filter(f => f.group === group).map(f => f.key);
        const allChecked = groupKeys.every(k => checkedFeatures.includes(k));
        setCheckedFeatures(prev =>
            allChecked
                ? prev.filter(k => !groupKeys.includes(k))
                : [...new Set([...prev, ...groupKeys])]
        );
        setDirty(true);
    };

    const selectAll = () => {
        setCheckedFeatures(ALL_FEATURES.map(f => f.key));
        setDirty(true);
    };
    const clearAll = () => {
        setCheckedFeatures([]);
        setDirty(true);
    };

    const handleSave = async () => {
        if (!selectedId) return;
        setSaving(true);
        try {
            await axiosInstance.put(`/roles/${selectedId}/features`, { features: checkedFeatures });
            setRoles(prev => prev.map(r => r.id === selectedId ? { ...r, features: checkedFeatures } : r));
            setDirty(false);
            showToast('Role permissions saved.');
        } catch {
            showToast('Failed to save permissions.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateRole = async () => {
        if (!newRoleName.trim()) return;
        setCreating(true);
        try {
            const res = await axiosInstance.post('/roles', { name: newRoleName.trim(), features: [] });
            setRoles(prev => [...prev, res.data]);
            setSelectedId(res.data.id);
            setCheckedFeatures([]);
            setDirty(false);
            setNewRoleName('');
            setShowNewRole(false);
            showToast(`Role "${res.data.name}" created.`);
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to create role.', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (role) => {
        if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
        try {
            await axiosInstance.delete(`/roles/${role.id}`);
            const updated = roles.filter(r => r.id !== role.id);
            setRoles(updated);
            if (selectedId === role.id) {
                const next = updated[0];
                setSelectedId(next?.id ?? null);
                setCheckedFeatures(next?.features ?? []);
            }
            showToast(`Role "${role.name}" deleted.`);
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete role.', 'error');
        }
    };

    const selectedRole = roles.find(r => r.id === selectedId);

    return (
        <div>
            {/* Toast */}
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a3a5c', margin: 0 }}>Role Management</h1>
                <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 14 }}>
                    Define roles and control exactly which features each role can access.
                </p>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60 }}><Spinner /></div>
            ) : (
                <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                    {/* Left: Role List */}
                    <div style={{ width: 240, flexShrink: 0 }}>
                        <div style={{
                            background: '#fff', borderRadius: 12,
                            border: '1px solid #e2e8f0', overflow: 'hidden',
                        }}>
                            <div style={{
                                padding: '14px 16px', borderBottom: '1px solid #f1f5f9',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Roles</span>
                                <button
                                    onClick={() => setShowNewRole(v => !v)}
                                    title="Create new role"
                                    style={{
                                        background: '#1B75F0', color: '#fff', border: 'none',
                                        borderRadius: 6, width: 24, height: 24, fontSize: 18, lineHeight: 1,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >+</button>
                            </div>

                            {showNewRole && (
                                <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', background: '#fafafa' }}>
                                    <input
                                        autoFocus
                                        value={newRoleName}
                                        onChange={e => setNewRoleName(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleCreateRole(); if (e.key === 'Escape') { setShowNewRole(false); setNewRoleName(''); } }}
                                        placeholder="Role name..."
                                        style={{
                                            width: '100%', padding: '7px 10px', borderRadius: 6,
                                            border: '1px solid #d1d5db', fontSize: 13, marginBottom: 6,
                                            outline: 'none', boxSizing: 'border-box',
                                        }}
                                    />
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <button
                                            onClick={handleCreateRole}
                                            disabled={creating || !newRoleName.trim()}
                                            style={{
                                                flex: 1, background: '#1B75F0', color: '#fff',
                                                border: 'none', borderRadius: 6, padding: '6px 0',
                                                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                            }}
                                        >
                                            {creating ? <Spinner /> : 'Create'}
                                        </button>
                                        <button
                                            onClick={() => { setShowNewRole(false); setNewRoleName(''); }}
                                            style={{
                                                flex: 1, background: '#f1f5f9', color: '#475569',
                                                border: 'none', borderRadius: 6, padding: '6px 0',
                                                fontSize: 12, cursor: 'pointer',
                                            }}
                                        >Cancel</button>
                                    </div>
                                </div>
                            )}

                            {roles.map(role => (
                                <div
                                    key={role.id}
                                    onClick={() => selectRole(role)}
                                    style={{
                                        padding: '11px 14px',
                                        background: selectedId === role.id ? '#F0F7FF' : 'transparent',
                                        borderLeft: selectedId === role.id ? '3px solid #1B75F0' : '3px solid transparent',
                                        cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        borderBottom: '1px solid #f8f8f8',
                                        transition: 'background 0.1s',
                                    }}
                                >
                                    <div>
                                        <div style={{
                                            fontSize: 13, fontWeight: selectedId === role.id ? 700 : 500,
                                            color: selectedId === role.id ? '#1B75F0' : '#374151',
                                        }}>
                                            {role.name}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                                            {role.features.length} feature{role.features.length !== 1 ? 's' : ''}
                                            {role.isSystem && ' · System'}
                                        </div>
                                    </div>
                                    {!role.isSystem && (
                                        <button
                                            onClick={e => { e.stopPropagation(); handleDelete(role); }}
                                            title="Delete role"
                                            style={{
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: '#d1d5db', fontSize: 16, padding: '2px 4px',
                                                borderRadius: 4,
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                                            onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}
                                        >×</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Feature Checklist */}
                    {selectedRole ? (
                        <div style={{ flex: 1 }}>
                            <div style={{
                                background: '#fff', borderRadius: 12,
                                border: '1px solid #e2e8f0', overflow: 'hidden',
                            }}>
                                {/* Header */}
                                <div style={{
                                    padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    flexWrap: 'wrap', gap: 10,
                                }}>
                                    <div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: '#1a3a5c' }}>
                                            {selectedRole.name}
                                            {selectedRole.isSystem && (
                                                <span style={{
                                                    marginLeft: 8, fontSize: 10, fontWeight: 700,
                                                    background: '#e0f2fe', color: '#0369a1',
                                                    padding: '2px 8px', borderRadius: 20,
                                                    verticalAlign: 'middle',
                                                }}>SYSTEM</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                                            {checkedFeatures.length} of {ALL_FEATURES.length} features enabled
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button onClick={selectAll} style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', cursor: 'pointer' }}>
                                            Select All
                                        </button>
                                        <button onClick={clearAll} style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', cursor: 'pointer' }}>
                                            Clear All
                                        </button>
                                        <button
                                            onClick={handleSave}
                                            disabled={saving || !dirty}
                                            style={{
                                                fontSize: 13, padding: '7px 20px', borderRadius: 8,
                                                border: 'none', fontWeight: 600, cursor: dirty ? 'pointer' : 'default',
                                                background: dirty ? '#1B75F0' : '#e5e7eb',
                                                color: dirty ? '#fff' : '#9ca3af',
                                                display: 'flex', alignItems: 'center', gap: 6,
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            {saving ? <><Spinner /> Saving…</> : 'Save Changes'}
                                        </button>
                                    </div>
                                </div>

                                {/* Feature Groups */}
                                <div style={{ padding: '20px' }}>
                                    {GROUPS.map(group => {
                                        const groupFeatures = ALL_FEATURES.filter(f => f.group === group);
                                        const allChecked = groupFeatures.every(f => checkedFeatures.includes(f.key));
                                        const someChecked = groupFeatures.some(f => checkedFeatures.includes(f.key));
                                        return (
                                            <div key={group} style={{ marginBottom: 24 }}>
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: 10,
                                                    marginBottom: 10,
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={allChecked}
                                                        ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                                        onChange={() => toggleGroup(group)}
                                                        style={{ width: 15, height: 15, accentColor: '#1B75F0', cursor: 'pointer' }}
                                                    />
                                                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#64748b' }}>
                                                        {group}
                                                    </span>
                                                    <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
                                                </div>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                                                    gap: '8px 16px',
                                                    paddingLeft: 24,
                                                }}>
                                                    {groupFeatures.map(feature => (
                                                        <label
                                                            key={feature.key}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 10,
                                                                cursor: 'pointer', padding: '8px 12px',
                                                                borderRadius: 8,
                                                                background: checkedFeatures.includes(feature.key) ? '#F0F7FF' : '#fafafa',
                                                                border: `1px solid ${checkedFeatures.includes(feature.key) ? '#f0d0b8' : '#f0f0f0'}`,
                                                                transition: 'all 0.1s',
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checkedFeatures.includes(feature.key)}
                                                                onChange={() => toggleFeature(feature.key)}
                                                                style={{ width: 15, height: 15, accentColor: '#1B75F0', cursor: 'pointer', flexShrink: 0 }}
                                                            />
                                                            <div>
                                                                <div style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                                                                    {feature.label}
                                                                </div>
                                                                <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>
                                                                    {feature.key}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 14 }}>
                            Select a role to manage its permissions.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RoleManagement;
